(function () {
  'use strict';

  if (window.UnifiedGA4Loader) {
    return;
  }

  const VERSION = '1.0.0';

  const SCRIPT_ID =
    'unified-ga4-gtag-script';

  const state = {
    status: 'idle',
    loading: false,
    loaded: false,
    configured: false,
    measurement_id: '',
    error: null,
    initialized_at: null,
    updated_at: null
  };

  /*
   * ------------------------------------------------------------
   * HELPERS
   * ------------------------------------------------------------
   */

  function clone(value) {
    try {
      return JSON.parse(
        JSON.stringify(value)
      );
    } catch (error) {
      return value;
    }
  }

  function nowIso() {
    return new Date()
      .toISOString();
  }

  function getConfig() {
    return (
      window.UnifiedTrackingConfig ||
      {}
    );
  }

  function getConsent() {
    return (
      window.UnifiedConsent ||
      null
    );
  }

  function isDebugEnabled() {
    return (
      getConfig().debug === true
    );
  }

  function log(
    message,
    data
  ) {
    if (!isDebugEnabled()) {
      return;
    }

    console.info(
      '[Unified GA4 Loader] ' +
        message,
      data || {}
    );
  }

  /*
   * ------------------------------------------------------------
   * EVENT EMISSION
   * ------------------------------------------------------------
   */

  function emit(
    type,
    data
  ) {
    if (
      window.UnifiedBus &&
      typeof window.UnifiedBus.emit ===
        'function'
    ) {
      try {
        return window.UnifiedBus.emit(
          type,
          data || {}
        );
      } catch (error) {
        // Diagnostic fallback below.
      }
    }

    try {
      window.dispatchEvent(
        new CustomEvent(
          'unified:debug',
          {
            detail: {
              type:
                type,

              timestamp:
                nowIso(),

              data:
                data || {}
            }
          }
        )
      );
    } catch (error) {
      // Best effort only.
    }

    return null;
  }

  /*
   * ------------------------------------------------------------
   * ERROR REPORTING
   * ------------------------------------------------------------
   */

  function reportError(
    severity,
    code,
    message,
    details
  ) {
    if (
      window.UnifiedErrors &&
      typeof window.UnifiedErrors.report ===
        'function'
    ) {
      try {
        return window.UnifiedErrors.report(
          severity,
          code,
          message,
          details || {}
        );
      } catch (error) {
        // Console fallback below.
      }
    }

    const method =
      severity === 'warning'
        ? 'warn'
        : 'error';

    console[method](
      '[Unified GA4 Loader] ' +
        message,
      details || {}
    );

    return null;
  }

  /*
   * ------------------------------------------------------------
   * GA4 CONFIG
   * ------------------------------------------------------------
   */

  function getGa4Config() {
    const providers =
      getConfig().providers ||
      {};

    const value =
      providers.ga4;

    /*
     * Legacy boolean support.
     *
     * true enables GA4 but cannot initialize
     * without a Measurement ID.
     */

    if (
      value === true
    ) {
      return {
        enabled: true,
        measurementId: ''
      };
    }

    if (
      value &&
      typeof value === 'object' &&
      !Array.isArray(value)
    ) {
      return {
        enabled:
          value.enabled === true,

        measurementId:
          String(
            value.measurementId ||
            ''
          ).trim()
      };
    }

    return {
      enabled: false,
      measurementId: ''
    };
  }

  function isValidMeasurementId(
    measurementId
  ) {
    return (
      /^G-[A-Z0-9]+$/i.test(
        String(
          measurementId ||
          ''
        ).trim()
      )
    );
  }

  /*
   * ------------------------------------------------------------
   * GTAG RUNTIME
   * ------------------------------------------------------------
   */

  function ensureGtagRuntime() {
    window.dataLayer =
      window.dataLayer ||
      [];

    if (
      typeof window.gtag !==
        'function'
    ) {
      window.gtag =
        function () {
          window.dataLayer.push(
            arguments
          );
        };
    }

    return true;
  }

  function setGaDisabled(
    measurementId,
    disabled
  ) {
    if (
      !measurementId
    ) {
      return;
    }

    window[
      'ga-disable-' +
        measurementId
    ] =
      disabled === true;
  }

  /*
   * ------------------------------------------------------------
   * GOOGLE CONSENT STATE
   * ------------------------------------------------------------
   */

  function setGoogleConsent(
    analyticsAllowed
  ) {
    if (
      typeof window.gtag !==
        'function'
    ) {
      return false;
    }

    window.gtag(
      'consent',
      'update',
      {
        analytics_storage:
          analyticsAllowed
            ? 'granted'
            : 'denied',

        ad_storage:
          'denied',

        ad_user_data:
          'denied',

        ad_personalization:
          'denied'
      }
    );

    return true;
  }

  /*
   * ------------------------------------------------------------
   * SCRIPT
   * ------------------------------------------------------------
   */

  function loadScript(
    measurementId
  ) {
    const existingScript =
      document.getElementById(
        SCRIPT_ID
      );

    if (existingScript) {
      state.loading =
        false;

      state.loaded =
        true;

      state.updated_at =
        nowIso();

      return existingScript;
    }

    const script =
      document.createElement(
        'script'
      );

    script.id =
      SCRIPT_ID;

    script.async =
      true;

    script.src =
      'https://www.googletagmanager.com/gtag/js?id=' +
      encodeURIComponent(
        measurementId
      );

    state.loading =
      true;

    state.status =
      'loading';

    state.updated_at =
      nowIso();

    script.addEventListener(
      'load',
      function () {
        state.loading =
          false;

        state.loaded =
          true;

        state.status =
          'loaded';

        state.updated_at =
          nowIso();

        emit(
          'ga4_script_loaded',
          {
            measurement_id:
              measurementId
          }
        );

        log(
          'Google tag script loaded',
          {
            measurementId:
              measurementId
          }
        );
      }
    );

    script.addEventListener(
      'error',
      function () {
        state.loading =
          false;

        state.loaded =
          false;

        state.status =
          'error';

        state.error = {
          code:
            'ga4_script_load_failed'
        };

        state.updated_at =
          nowIso();

        reportError(
          'error',
          'ga4_script_load_failed',
          'Google tag script failed to load.',
          {
            measurement_id:
              measurementId
          }
        );

        emit(
          'ga4_script_failed',
          {
            measurement_id:
              measurementId
          }
        );
      }
    );

    document.head.appendChild(
      script
    );

    return script;
  }

  /*
   * ------------------------------------------------------------
   * INITIALIZE GA4
   * ------------------------------------------------------------
   */

  function initializeGa4(
    source
  ) {
    const ga4 =
      getGa4Config();

    /*
     * Provider must be explicitly enabled.
     */

    if (
      ga4.enabled !== true
    ) {
      state.status =
        'disabled';

      state.updated_at =
        nowIso();

      return {
        success: false,
        reason: 'provider_disabled'
      };
    }

    /*
     * Measurement ID must exist.
     */

    if (
      !isValidMeasurementId(
        ga4.measurementId
      )
    ) {
      state.status =
        'invalid_config';

      state.error = {
        code:
          'ga4_measurement_id_invalid'
      };

      state.updated_at =
        nowIso();

      reportError(
        'error',
        'ga4_measurement_id_invalid',
        'GA4 provider is enabled but Measurement ID is missing or invalid.',
        {}
      );

      return {
        success: false,
        reason:
          'measurement_id_invalid'
      };
    }

    state.measurement_id =
      ga4.measurementId;

    /*
     * Consent Manager must exist
     * and be ready.
     */

    const consent =
      getConsent();

    if (
      !consent ||
      typeof consent.isReady !==
        'function' ||
      consent.isReady() !==
        true
    ) {
      state.status =
        'waiting_for_consent';

      state.updated_at =
        nowIso();

      return {
        success: false,
        reason:
          'consent_not_ready'
      };
    }

    /*
     * Fail closed.
     *
     * Google tag is NOT loaded before
     * analytics consent.
     */

    if (
      typeof consent.analyticsAllowed !==
        'function' ||
      consent.analyticsAllowed() !==
        true
    ) {
      setGaDisabled(
        ga4.measurementId,
        true
      );

      if (
        typeof window.gtag ===
          'function'
      ) {
        setGoogleConsent(
          false
        );
      }

      state.status =
        'blocked_by_consent';

      state.updated_at =
        nowIso();

      emit(
        'ga4_blocked_by_consent',
        {
          source:
            source ||
            'unknown'
        }
      );

      return {
        success: false,
        reason:
          'analytics_consent_denied'
      };
    }

    /*
     * Analytics consent granted.
     */

    setGaDisabled(
      ga4.measurementId,
      false
    );

    /*
     * If GA4 was already configured,
     * only restore consent state.
     */

    if (
      state.configured === true
    ) {
      setGoogleConsent(
        true
      );

      state.status =
        'ready';

      state.updated_at =
        nowIso();

      emit(
        'ga4_consent_restored',
        {
          source:
            source ||
            'unknown',

          measurement_id:
            ga4.measurementId
        }
      );

      return {
        success: true,
        reason:
          'already_configured'
      };
    }

    ensureGtagRuntime();

    /*
     * Conservative Google Consent Mode state.
     *
     * Analytics is granted because Shopify
     * has explicitly allowed analytics.
     *
     * Advertising storage and personalization
     * remain denied. Google Ads is handled
     * separately later.
     */

    window.gtag(
      'consent',
      'default',
      {
        analytics_storage:
          'denied',

        ad_storage:
          'denied',

        ad_user_data:
          'denied',

        ad_personalization:
          'denied'
      }
    );

    setGoogleConsent(
      true
    );

    /*
     * Standard gtag initialization.
     */

    window.gtag(
      'js',
      new Date()
    );

    window.gtag(
      'config',
      ga4.measurementId,
      {
        debug_mode:
          isDebugEnabled()
      }
    );

    /*
     * Load Google's library only AFTER
     * analytics consent has been granted.
     */

    loadScript(
      ga4.measurementId
    );

    state.configured =
      true;

    state.status =
      'ready';

    state.error =
      null;

    if (
      !state.initialized_at
    ) {
      state.initialized_at =
        nowIso();
    }

    state.updated_at =
      nowIso();

    emit(
      'ga4_initialized',
      {
        source:
          source ||
          'unknown',

        measurement_id:
          ga4.measurementId
      }
    );

    log(
      'GA4 initialized',
      {
        source:
          source ||
          'unknown',

        measurementId:
          ga4.measurementId
      }
    );

    return {
      success: true,
      reason:
        'ga4_initialized'
    };
  }

  /*
   * ------------------------------------------------------------
   * CONSENT SYNCHRONIZATION
   * ------------------------------------------------------------
   */

  function sync(
    source
  ) {
    return initializeGa4(
      source ||
      'manual_sync'
    );
  }

  function handleConsentChange() {
    /*
     * UnifiedConsent registered its Shopify
     * listener before this loader.
     *
     * Defer one task as an additional safeguard
     * so we always read the refreshed cached state.
     */

    window.setTimeout(
      function () {
        sync(
          'visitorConsentCollected'
        );
      },
      0
    );
  }

  /*
   * ------------------------------------------------------------
   * STATUS
   * ------------------------------------------------------------
   */

  function getStatus() {
    const ga4 =
      getGa4Config();

    const consent =
      getConsent();

    return {
      version:
        VERSION,

      enabled:
        ga4.enabled,

      measurement_id:
        ga4.measurementId,

      measurement_id_valid:
        isValidMeasurementId(
          ga4.measurementId
        ),

      consent_ready:
        Boolean(
          consent &&
          typeof consent.isReady ===
            'function' &&
          consent.isReady() ===
            true
        ),

      analytics_allowed:
        Boolean(
          consent &&
          typeof consent.analyticsAllowed ===
            'function' &&
          consent.analyticsAllowed() ===
            true
        ),

      gtag_available:
        typeof window.gtag ===
          'function',

      script_present:
        Boolean(
          document.getElementById(
            SCRIPT_ID
          )
        ),

      loading:
        state.loading,

      loaded:
        state.loaded,

      configured:
        state.configured,

      status:
        state.status,

      error:
        clone(
          state.error
        ),

      initialized_at:
        state.initialized_at,

      updated_at:
        state.updated_at
    };
  }

  /*
   * ------------------------------------------------------------
   * PUBLIC API
   * ------------------------------------------------------------
   */

  window.UnifiedGA4Loader = {
    version:
      VERSION,

    sync:
      sync,

    getStatus:
      getStatus
  };

  /*
   * ------------------------------------------------------------
   * CONSENT EVENTS
   * ------------------------------------------------------------
   */

  document.addEventListener(
    'visitorConsentCollected',
    handleConsentChange
  );

  /*
   * ------------------------------------------------------------
   * INITIALIZATION
   * ------------------------------------------------------------
   */

  const consent =
    getConsent();

  if (
    consent &&
    typeof consent.whenReady ===
      'function'
  ) {
    consent.whenReady(
      function () {
        sync(
          'consent_ready'
        );
      }
    );
  } else {
    state.status =
      'consent_manager_unavailable';

    state.updated_at =
      nowIso();

    reportError(
      'error',
      'ga4_consent_manager_unavailable',
      'Unified Consent is unavailable. GA4 will not load.',
      {}
    );
  }

  log(
    'GA4 Loader initialized',
    {
      version:
        VERSION
    }
  );
})();